import React from "react";
import { Button, Form, Tab, Tabs } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import ContentSettings from "./ContentSettings";

import { alertActions } from "../../features/alert/alertSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { updateSettings } from "../../lib/api";
import { ISiteSettingsForm } from "../../types/forms";

const PageContent = (): React.JSX.Element | undefined => {
    const settings = useAppSelector(state => state.global.settings);
    const dispatch = useAppDispatch();

    const settingsToShow = {
        homePage: [
            "majorybl_home_page_blue_box",
            "majorybl_home_page_red_box",
            "majorybl_home_page_split_left",
            "majorybl_home_page_split_right",
        ],
        lookingForPlayers: [
            "majorybl_looking_for_players_text",
            "majorybl_lfp_page_content",
        ],
        tryouts: ["majorybl_tryout_text", "majorybl_tryout_page_content"],
        registration: [
            "majorybl_team_registration_content",
            "majorybl_coach_registration_content",
            "majorybl_orguser_registration_content",
        ],
    };

    const methods = useForm<ISiteSettingsForm>({
        mode: "onChange",
        defaultValues: {
            content: Object.keys(settingsToShow).reduce(
                (acc, page) => {
                    const settingValues =
                        settingsToShow[page as keyof typeof settingsToShow];
                    return settingValues.reduce((subAcc, settingName) => {
                        const setting = settings.find(
                            s => s.settingName === settingName,
                        );
                        if (setting) {
                            subAcc[settingName] = setting.setting;
                        }
                        return subAcc;
                    }, acc);
                },
                {} as Record<string, any>,
            ),
        },
    });

    const onSubmit: SubmitHandler<Partial<ISiteSettingsForm>> = async data => {
        const dirtyFields = methods.formState.dirtyFields;

        const filteredData: Partial<ISiteSettingsForm> = {
            content: {},
        };

        (Object.keys(dirtyFields) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                const dirtySubfields = dirtyFields[category];
                Object.keys(dirtySubfields).forEach(subfield => {
                    if (data[category]?.hasOwnProperty(subfield)) {
                        filteredData[category][subfield] =
                            data[category][subfield];
                    }
                });
            },
        );

        (Object.keys(filteredData) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                if (Object.keys(filteredData[category]).length === 0) {
                    delete filteredData[category];
                }
            },
        );

        await updateSettings(filteredData)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: "Settings updated successfully",
                        dismissible: true,
                    }),
                );

                methods.reset(data);
            })
            .catch(e =>
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem updating the settings.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                ),
            );
    };

    return (
        <section className="siteSettingsContent">
            <FormProvider {...methods}>
                <Form onSubmit={methods.handleSubmit(onSubmit)}>
                    <Tabs
                        defaultActiveKey="homepage"
                        id="siteSettings"
                        data-testid="siteSettingsContent"
                    >
                        <Tab eventKey="homepage" title="Home Page">
                            <ContentSettings
                                settings={settings.filter(
                                    s => s.category === "content",
                                )}
                                category="content"
                                settingsToShow={settingsToShow.homePage}
                            />
                        </Tab>
                        <Tab
                            eventKey="lookingForPlayers"
                            title="Looking For Players"
                        >
                            <ContentSettings
                                settings={settings.filter(
                                    s => s.category === "content",
                                )}
                                category="content"
                                settingsToShow={
                                    settingsToShow.lookingForPlayers
                                }
                            />
                        </Tab>
                        <Tab eventKey="tryouts" title="Tryouts">
                            <ContentSettings
                                settings={settings.filter(
                                    s => s.category === "content",
                                )}
                                category="content"
                                settingsToShow={settingsToShow.tryouts}
                            />
                        </Tab>
                        <Tab eventKey="registration" title="Registration">
                            <ContentSettings
                                settings={settings.filter(
                                    s => s.category === "content",
                                )}
                                category="content"
                                settingsToShow={settingsToShow.registration}
                            />
                        </Tab>
                    </Tabs>
                    <Button variant="primary" type="submit">
                        Update Settings
                    </Button>
                </Form>
            </FormProvider>
        </section>
    );
};

export default PageContent;
